import { useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useContentToken,
  usePropertyField,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { FeeGuideModal } from './FeeGuideModal';
import { useFeeGuideAimCode } from '../lib/feeGuide';
import { isPriceGuaranteeEnabled } from '../lib/priceGuarantee';

/**
 * Floor-plans “Our True Price Guarantee” section (Duda black split layout).
 * Only renders when custom_show_price_guarantee is true.
 */
export function TruePriceGuarantee() {
  const enabled = isPriceGuaranteeEnabled(usePropertyField('custom_show_price_guarantee'));
  const propertyName = usePropertyField('property_name');
  const title = useContentToken('truepriceguarantee.title');
  const lead = useContentToken('truepriceguarantee.lead');
  const summary = useContentToken('truepriceguarantee.summary');
  const ctaLabel = useContentToken('truepriceguarantee.fee_guide_cta_label');
  const pillar1Title = useContentToken('truepriceguarantee.pillar1_title');
  const pillar1Subtitle = useContentToken('truepriceguarantee.pillar1_subtitle');
  const pillar2Title = useContentToken('truepriceguarantee.pillar2_title');
  const pillar2Subtitle = useContentToken('truepriceguarantee.pillar2_subtitle');
  const pillar3Title = useContentToken('truepriceguarantee.pillar3_title');
  const pillar3Subtitle = useContentToken('truepriceguarantee.pillar3_subtitle');
  const watermarkToken = useContentToken('truepriceguarantee.watermark_letter');
  const hasFeeGuideAimCode = Boolean(useFeeGuideAimCode());
  const [feeGuideOpen, setFeeGuideOpen] = useState(false);

  if (!enabled) return null;

  const displayTitle = String(title || 'Our True Price Guarantee');
  const watermark =
    String(watermarkToken || '').trim() ||
    (typeof propertyName === 'string' && propertyName.trim()
      ? propertyName.trim().charAt(0).toUpperCase()
      : 'A');

  return (
    <>
      {/* Same horizontal inset as HeroSolidBG so the black band lines up with the hero image. */}
      <div className="tpg-shell">
      <section className="tpg" aria-label={displayTitle}>
        <div className="tpg-inner">
          <div className="tpg-copy">
            <EditableToken
              path="truepriceguarantee.title"
              defaultValue="Our True Price Guarantee"
              as="h2"
              className="tpg-title font-heading"
            >
              {displayTitle}
            </EditableToken>

            <div className="tpg-block">
              <EditableToken
                path="truepriceguarantee.lead"
                defaultValue="What you see is what you pay."
                as="p"
                className="tpg-heading font-body"
              >
                {lead || 'What you see is what you pay.'}
              </EditableToken>
              <RichTextContent
                path="truepriceguarantee.intro"
                defaultValue="<p>No hidden fees. No surprises. Just one clear, monthly price that includes rent and all other mandatory monthly costs. The only additional charges are for optional add-ons and utilities—your choice, always. Utilities, which can be found on our fee disclosure page.</p>"
                as="div"
                className="tpg-body font-body"
              />
            </div>

            <div className="tpg-block">
              <EditableToken
                path="truepriceguarantee.pillar1_title"
                defaultValue="Comprehensive."
                as="p"
                className="tpg-heading font-body"
              >
                {pillar1Title || 'Comprehensive.'}
              </EditableToken>
              <EditableToken
                path="truepriceguarantee.pillar1_subtitle"
                defaultValue="Everything you need, already included."
                as="p"
                className="tpg-heading font-body"
              >
                {pillar1Subtitle || 'Everything you need, already included.'}
              </EditableToken>
              <RichTextContent
                path="truepriceguarantee.pillar1_body"
                defaultValue="<p>From maintenance and management to community amenities, it’s all covered. No fine print. No guesswork.</p>"
                as="div"
                className="tpg-body font-body"
              />
            </div>

            <div className="tpg-block">
              <EditableToken
                path="truepriceguarantee.pillar2_title"
                defaultValue="All Yours."
                as="p"
                className="tpg-heading font-body"
              >
                {pillar2Title || 'All Yours.'}
              </EditableToken>
              <EditableToken
                path="truepriceguarantee.pillar2_subtitle"
                defaultValue="Your space. Your terms. Your peace of mind."
                as="p"
                className="tpg-heading font-body"
              >
                {pillar2Subtitle || 'Your space. Your terms. Your peace of mind.'}
              </EditableToken>
              <RichTextContent
                path="truepriceguarantee.pillar2_body"
                defaultValue="<p>Your home should reflect your lifestyle. That’s why, beyond your rent, we offer optional add-ons so you can personalize your living experience—only if you want to.</p>"
                as="div"
                className="tpg-body font-body"
              />
            </div>

            <div className="tpg-block">
              <EditableToken
                path="truepriceguarantee.pillar3_title"
                defaultValue="All Set."
                as="p"
                className="tpg-heading font-body"
              >
                {pillar3Title || 'All Set.'}
              </EditableToken>
              <EditableToken
                path="truepriceguarantee.pillar3_subtitle"
                defaultValue="Clarity you can count on."
                as="p"
                className="tpg-heading font-body"
              >
                {pillar3Subtitle || 'Clarity you can count on.'}
              </EditableToken>
              <RichTextContent
                path="truepriceguarantee.pillar3_body"
                defaultValue="<p>With a straightforward monthly price and no last-minute charges, you can plan ahead, budget confidently, and focus on enjoying your home.</p>"
                as="div"
                className="tpg-body font-body"
              />
            </div>
          </div>

          <div className="tpg-aside" aria-hidden="true">
            <span className="tpg-watermark">{watermark}</span>
            <div className="tpg-aside-text">
              <p className="tpg-aside-title font-heading">{displayTitle}</p>
              <EditableToken
                path="truepriceguarantee.summary"
                defaultValue="Comprehensive. All Yours. All Set."
                as="p"
                className="tpg-aside-summary font-body"
              >
                {summary || 'Comprehensive. All Yours. All Set.'}
              </EditableToken>
            </div>
          </div>
        </div>
      </section>
      </div>

      {hasFeeGuideAimCode ? (
        <>
          <div className="tpg-cta-row">
            <button
              type="button"
              onClick={() => setFeeGuideOpen(true)}
              className="tpg-cta font-body"
            >
              <EditableToken
                path="truepriceguarantee.fee_guide_cta_label"
                defaultValue="Property Fee Guide"
                as="span"
              >
                {ctaLabel || 'Property Fee Guide'}
              </EditableToken>
            </button>
          </div>

          <FeeGuideModal
            open={feeGuideOpen}
            onClose={() => setFeeGuideOpen(false)}
            title={String(ctaLabel || 'Property Fee Guide')}
          />
        </>
      ) : null}

      <style>{`
        /* Match HeroSolidBG: px-3 sm:px-5 md:px-6 */
        .tpg-shell {
          width: 100%;
          padding: 0 0.75rem;
          background: var(--vibe-color-background, #fff);
          box-sizing: border-box;
        }
        @media (min-width: 640px) {
          .tpg-shell { padding: 0 1.25rem; }
        }
        @media (min-width: 768px) {
          .tpg-shell { padding: 0 1.5rem; }
        }
        .tpg {
          width: 100%;
          background: #000;
          color: #fff;
          padding: 3.5rem 1.25rem;
          box-sizing: border-box;
        }
        @media (min-width: 768px) {
          .tpg { padding: 4.5rem 2rem; }
        }
        .tpg-inner {
          width: 100%;
          display: grid;
          gap: 2.5rem;
        }
        @media (min-width: 1024px) {
          .tpg-inner {
            grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
            gap: 3rem;
            align-items: center;
          }
        }
        .tpg-title {
          margin: 0 0 1.75rem;
          font-size: clamp(1.75rem, 3vw, 2.25rem);
          font-weight: 400;
          line-height: 1.2;
          color: #fff;
        }
        .tpg-block { margin-bottom: 1.5rem; }
        .tpg-block:last-child { margin-bottom: 0; }
        .tpg-heading {
          margin: 0;
          font-size: 1.05rem;
          font-weight: 700;
          line-height: 1.35;
          color: #fff;
        }
        .tpg-body {
          margin-top: 0.35rem;
          font-size: 0.95rem;
          line-height: 1.55;
          color: #fff;
        }
        .tpg-body p { margin: 0; }
        .tpg-aside {
          position: relative;
          min-height: 16rem;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        @media (min-width: 1024px) {
          .tpg-aside { min-height: 36rem; }
        }
        /* Serif watermark to echo Duda’s letter mark; still the property initial. */
        .tpg-watermark {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: Georgia, 'Times New Roman', 'Liberation Serif', serif;
          font-size: clamp(16rem, 42vw, 28rem);
          font-weight: 700;
          line-height: 0.85;
          letter-spacing: -0.04em;
          /* Soft charcoal on black — readable but still watermark-like */
          color: rgba(255, 255, 255, 0.12);
          pointer-events: none;
          user-select: none;
        }
        @media (min-width: 1024px) {
          .tpg-watermark {
            font-size: clamp(28rem, 38vw, 40rem);
          }
        }
        .tpg-aside-text {
          position: relative;
          z-index: 1;
          text-align: center;
          padding: 1rem;
          max-width: 18rem;
        }
        .tpg-aside-title {
          margin: 0;
          font-size: clamp(1.75rem, 3.2vw, 2.75rem);
          font-weight: 700;
          line-height: 1.1;
          color: #fff;
        }
        .tpg-aside-summary {
          margin: 0.85rem 0 0;
          font-size: 1rem;
          line-height: 1.4;
          color: #fff;
        }
        .tpg-cta-row {
          display: flex;
          justify-content: center;
          padding: 2rem 0.75rem 2.5rem;
          background: var(--vibe-color-background, #fff);
          box-sizing: border-box;
        }
        @media (min-width: 640px) {
          .tpg-cta-row { padding-left: 1.25rem; padding-right: 1.25rem; }
        }
        @media (min-width: 768px) {
          .tpg-cta-row { padding-left: 1.5rem; padding-right: 1.5rem; }
        }
        .tpg-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 12rem;
          padding: 0.85rem 1.5rem;
          border: 1px solid var(--vibe-color-text, #1a1a1a);
          border-radius: 0.25rem;
          background: var(--vibe-color-text, #1a1a1a);
          color: #fff;
          font-size: 0.9rem;
          font-weight: 400;
          cursor: pointer;
          transition: background 0.2s, color 0.2s;
        }
        .tpg-cta:hover {
          background: #fff;
          color: var(--vibe-color-text, #1a1a1a);
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'TruePriceGuarantee',
  displayName: 'True Price Guarantee',
  tokens: {
    'truepriceguarantee.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Our True Price Guarantee',
    },
    'truepriceguarantee.lead': {
      label: 'Lead',
      type: 'text',
      defaultValue: 'What you see is what you pay.',
    },
    'truepriceguarantee.intro': {
      label: 'Intro',
      type: 'richtext',
      defaultValue:
        '<p>No hidden fees. No surprises. Just one clear, monthly price that includes rent and all other mandatory monthly costs. The only additional charges are for optional add-ons and utilities—your choice, always. Utilities, which can be found on our fee disclosure page.</p>',
    },
    'truepriceguarantee.pillar1_title': {
      label: 'Pillar 1 Title',
      type: 'text',
      defaultValue: 'Comprehensive.',
    },
    'truepriceguarantee.pillar1_subtitle': {
      label: 'Pillar 1 Subtitle',
      type: 'text',
      defaultValue: 'Everything you need, already included.',
    },
    'truepriceguarantee.pillar1_body': {
      label: 'Pillar 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>From maintenance and management to community amenities, it’s all covered. No fine print. No guesswork.</p>',
    },
    'truepriceguarantee.pillar2_title': {
      label: 'Pillar 2 Title',
      type: 'text',
      defaultValue: 'All Yours.',
    },
    'truepriceguarantee.pillar2_subtitle': {
      label: 'Pillar 2 Subtitle',
      type: 'text',
      defaultValue: 'Your space. Your terms. Your peace of mind.',
    },
    'truepriceguarantee.pillar2_body': {
      label: 'Pillar 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Your home should reflect your lifestyle. That’s why, beyond your rent, we offer optional add-ons so you can personalize your living experience—only if you want to.</p>',
    },
    'truepriceguarantee.pillar3_title': {
      label: 'Pillar 3 Title',
      type: 'text',
      defaultValue: 'All Set.',
    },
    'truepriceguarantee.pillar3_subtitle': {
      label: 'Pillar 3 Subtitle',
      type: 'text',
      defaultValue: 'Clarity you can count on.',
    },
    'truepriceguarantee.pillar3_body': {
      label: 'Pillar 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>With a straightforward monthly price and no last-minute charges, you can plan ahead, budget confidently, and focus on enjoying your home.</p>',
    },
    'truepriceguarantee.summary': {
      label: 'Aside Summary',
      type: 'text',
      defaultValue: 'Comprehensive. All Yours. All Set.',
    },
    'truepriceguarantee.watermark_letter': {
      label: 'Watermark Letter',
      type: 'text',
      defaultValue: '',
      description: 'Large background letter. Leave blank to use the first letter of the property name.',
    },
    'truepriceguarantee.fee_guide_cta_label': {
      label: 'Fee Guide CTA Label',
      type: 'text',
      defaultValue: 'Property Fee Guide',
    },
  },
};

(TruePriceGuarantee as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default TruePriceGuarantee;
